import { useRef } from 'react';
import { Row, Col, Popover, OverlayTrigger } from 'react-bootstrap';
import { Locale, ScienceStat } from '../i18n/types';

interface Props {
  science: Locale['science'];
}

export default function ScienceTab({ science }: Props) {
  const triggerRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const renderTooltip = (stat: ScienceStat) => (
    <Popover className="stat-popover">
      <Popover.Body>
        <p className="text-body-default mb-0">{stat.tooltip}</p>
      </Popover.Body>
    </Popover>
  );

  return (
    <div>
      <h2 className="tab-page-title">{science.title}</h2>
      <p className="text-intro">{science.intro}</p>

      <div className="d-flex flex-column gap-3">
        {science.sections.map((s: any) => (
          <div key={s.title}>
            <h3 className="section-title mb-3">{s.title}</h3>
            {s.isHtml
              ? <p className="text-body" dangerouslySetInnerHTML={{ __html: s.desc }} />
              : <p className="text-body">{s.desc}</p>
            }
          </div>
        ))}

        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #2a2a2a' }}>
          <h3 className="label-uppercase mb-3">{science.statsLabel}</h3>
          <Row className="g-3">
            {science.stats.map((stat, i) => (
              <Col md={6} key={i}>
                <div className="card-dark-compact p-3 position-relative">
                  <OverlayTrigger
                    trigger="click"
                    placement="top"
                    overlay={renderTooltip(stat)}
                    rootClose
                  >
                    <button
                      ref={(el) => {
                        if (el) triggerRefs.current.set(i, el);
                      }}
                      className="stat-info-btn"
                      aria-label="więcej informacji"
                    >
                      ⓘ
                    </button>
                  </OverlayTrigger>
                  <div className="stat-value mb-2">{stat.value}</div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-sub">{stat.sub}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        <div className="mt-3 pt-3 divider-dark">
          <div className="text-secondary text-small">
            <div className="mb-1 label-uppercase-small">{science.referencesLabel}</div>
            <div>Jeukendrup AE. Nutrition for endurance sports: Marathon, triathlon, and road cycling. <em>Journal of Sports Sciences</em>, 2011; 29(S1): S91-S99.</div>
            <div>Jeukendrup AE, Jentjens R. Oxidation of carbohydrate feedings during prolonged exercise: current thoughts, guidelines and directions for future research. <em>Sports Medicine</em>, 2000; 29(6): 407-424.</div>
            <div>Jentjens RL, Jeukendrup AE. High rates of exogenous carbohydrate oxidation from a mixture of glucose and fructose ingested during prolonged cycling exercise. <em>British Journal of Nutrition</em>, 2005; 93(4): 485-492.</div>
            <div>Burke LM et al. Carbohydrates for training and competition. <em>Journal of Sports Sciences</em>, 2011; 29(S1): S17-S27.</div>
            <div>Coggan AR, Allen H. <em>Training and Racing with a Power Meter.</em> VeloPress, 2010.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
